import { FullscreenIcon } from "./FullscreenIcon";
import { useDialog } from "./Dialogs";
// 等扫码：订阅 /ws scan_frame 显示二维码画面（大号、可全屏），刷新二维码 / 我已扫码
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ScanState } from "@csp/shared";
import { api } from "../lib/api";
import { useAction } from "../lib/hooks";
import { useRealtime, useWsMessages } from "../lib/realtime";

interface Frame {
  src: string;
  /** 只截二维码的图 */
  qr?: string;
  state?: ScanState;
  refreshLeft?: number;
  reloginLeft?: number;
  at: string;
  expired: boolean;
  sessionExpired?: boolean;
  expiresAt?: string | null;
  expirySource?: "page" | "estimated";
  /** 银行授权码（申请人手机相机扫，不会过期） */
  authLink?: boolean;
}

export function ScanPanel({ taskId, canAct, who = null }: { taskId: string; canAct: boolean; /** Corppass 任务分配的人：由他扫 */ who?: string | null }) {
  const rt = useRealtime();
  const dialog = useDialog();
  const [open, setOpen] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [frame, setFrame] = useState<Frame | null>(null);
  /** 点了重新登录、弹框已关：等新二维码出来再弹出 */
  const [waitQr, setWaitQr] = useState(false);
  /** 重新登录请求被后端接受的时间：之后才来的过期帧才算「重新登录没拿到新码」 */
  const reloginAt = useRef(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const act = useAction();

  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 250); return () => clearInterval(timer); }, []);

  useEffect(() => {
    if (!canAct || (!open && !waitQr)) return;
    const off = rt.subscribeScan(taskId);
    // 进入页面立刻要一帧（订阅消息先发出去）
    const t = setTimeout(() => void api.scanFrame(taskId).catch(() => undefined), 150);
    return () => {
      clearTimeout(t);
      off();
    };
  }, [taskId, canAct, open, waitQr, rt]);

  useWsMessages((m) => {
    if (m.type === "scan_frame" && m.taskId === taskId) {
      // 新二维码出来就弹出；重新登录后回来的仍是过期 / 会话过期（没拿到新码）也弹出，让人看到原因、再点重新登录
      const afterRelogin = reloginAt.current > 0 && Date.parse(m.at) > reloginAt.current;
      if (waitQr && ((m.state === "qr_active" && m.imageBase64) || (afterRelogin && m.state && m.state !== "qr_active" && m.state !== "awaiting_confirm" && m.state !== "logged_in"))) { setWaitQr(false); setOpen(true); }
      setFrame({ src: m.imageBase64 ? `data:${m.mime};base64,${m.imageBase64}` : "", qr: m.qrBase64 ? `data:image/png;base64,${m.qrBase64}` : "", at: m.at, state: m.state, refreshLeft: m.refreshLeft, reloginLeft: m.reloginLeft, expired: m.expired, sessionExpired: m.sessionExpired, expiresAt: m.expiresAt, expirySource: m.expirySource, authLink: m.authLink });
    }
  });

  const seconds = frame?.expiresAt ? Math.max(0, Math.ceil((new Date(frame.expiresAt).getTime() - now) / 1000)) : null;
  // 页面状态（规范 §4）：旧后端没有 state 时按 expired / sessionExpired 推断
  const state: ScanState | null = frame
    ? frame.state === "qr_active" && seconds === 0 ? "qr_expired" : frame.state ?? (frame.sessionExpired ? "session_expired" : frame.expired || seconds === 0 ? "qr_expired" : "qr_active")
    : null;
  const expired = state === "qr_expired" || state === "session_expired";
  const deadEnd = state === "session_expired" || state === "tab_missing";
  // 人点刷新不限次数
  const canRefresh = state === "qr_active" || state === "qr_expired";
  const canRelogin = (frame?.reloginLeft ?? 1) > 0;
  const authLink = !!frame?.authLink;
  const tip = authLink ? (state === "logged_in" ? "✅ 已完成授权，正在继续…" : "📱 请申请人用手机相机扫码授权（不是 Singpass App），这个码不会过期") : state === "awaiting_confirm" ? "✅ 已扫码，请在手机 Singpass App 上点确认" : state === "logged_in" ? "✅ 已登录，正在继续…" : "📱 请用手机 Singpass App 扫码";
  const banner =
    state === "session_expired" ? "登录会话已过期，旧二维码不能再用，请点击重新登录"
    : state === "tab_missing" ? "扫码页已被关闭，请点击重新登录取得新二维码"
    : state === "qr_expired" ? "二维码已过期，请点击刷新二维码"
    : null;

  // 二维码单独放大显示；「复制二维码」把图放进剪贴板，可直接粘到 WhatsApp / 微信发给扫码人
  const bigQr = !!frame?.qr && state === "qr_active";
  const [copied, setCopied] = useState(false);
  const [copyErr, setCopyErr] = useState<string | null>(null);
  const copyQr = async () => {
    setCopyErr(null);
    try {
      const blob = await (await fetch(frame!.qr!)).blob();
      if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) throw new Error("这个浏览器不支持复制图片，请在二维码上右键「复制图片」");
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (e) {
      setCopyErr((e as Error).message || "复制失败");
    }
  };

  // 重新登录：弹框马上关掉，在实时画面看浏览器重新登录；新二维码出来再自动弹出（失败就弹回来显示原因）
  const relogin = async () => {
    setWaitQr(true);
    setOpen(false);
    setFrame(null);
    reloginAt.current = 0;
    if ((await act.run("restart", () => api.scanRestart(taskId))) === undefined) { setWaitQr(false); setOpen(true); }
    else reloginAt.current = Date.now();
  };

  const fullscreen = () => {
    const el = boxRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.().catch(() => undefined);
  };

  if (!canAct) return <div className="notice warn">正在等{who ?? "文员"}扫码登录 Singpass。</div>;

  return (
    <>
    {who && <span className="scan-who-inline">请 <b>{who}</b> 扫码</span>}
    <button className="btn btn-primary" onClick={() => setOpen(true)}>打开扫码窗口</button>
    {waitQr && <span className="small muted" data-testid="relogin-wait">正在重新登录，可在上面实时画面看浏览器；新二维码出来会自动弹出扫码窗口</span>}
    {/* 弹框挂到 body 上：放在任务页里会被「画面全屏」（下半部分 display:none）或切走的标签页一起藏掉 */}
    {open && createPortal(<div className="modal-mask"><div className="modal" role="dialog" aria-modal="true" aria-label="Singpass 扫码登录" style={{ maxWidth: 900 }}>
    <h2>{authLink ? "申请人扫码授权" : "Singpass 扫码登录"}</h2>
    {who && <div className="scan-who" data-testid="scan-who">请 <b>{who}</b> 用自己的手机扫码</div>}
    <div className="scan-box" ref={boxRef} data-testid="scan-panel">
      <div className="scan-tip" data-testid="scan-tip" data-state={state ?? ""}>{tip}</div>
      {bigQr && (
        <div className="scan-qr" data-testid="scan-qr">
          <img src={frame!.qr} alt="Singpass 二维码" />
          <div className="row" style={{ justifyContent: "center" }}>
            <button className="btn btn-primary btn-big" data-testid="copy-qr" onClick={() => void copyQr()}>{copied ? "✓ 已复制，去 WhatsApp / 微信粘贴" : "📋 复制二维码"}</button>
          </div>
          {copyErr && <div className="small err">{copyErr}</div>}
        </div>
      )}
      <div className={`scan-frame${bigQr ? " scan-frame-small" : ""}`}>
        {frame?.src ? <img src={frame.src} alt="Singpass 二维码画面" data-testid="scan-frame" /> : <div className="muted" style={{ padding: 40 }}>{frame ? "没有画面" : "正在获取二维码画面…"}</div>}
        {banner && <div className="expired" data-testid="scan-banner">{banner}</div>}
      </div>
      {state === "qr_active" && seconds !== null && <div data-testid="qr-countdown">{frame?.expirySource === "page" ? "剩余有效时间" : "预计剩余有效时间"}：{seconds} 秒</div>}
      {frame && <div className="small muted">画面更新于 {new Date(frame.at).toLocaleTimeString("zh-CN", { hour12: false })}</div>}
      {act.error && <div className="notice err">{act.error}</div>}
      <div className="row">
        {!authLink && <><button className={`btn btn-big${expired && canRefresh ? " btn-primary" : ""}`} disabled={!!act.busy || !canRefresh} onClick={() => void act.run("refresh", async () => { await api.scanRefresh(taskId); await api.scanFrame(taskId); return { ok: true }; })}>
          {act.busy === "refresh" ? "刷新中…" : "🔄 刷新二维码"}
        </button>
        <button className={`btn btn-big${deadEnd || (expired && !canRefresh) ? " btn-primary" : ""}`} disabled={!!act.busy || !canRelogin} onClick={() => void relogin()}>
          {act.busy === "restart" ? "正在重新登录…" : "重新登录"}
        </button></>}
        <button className="btn btn-ok btn-big" disabled={!!act.busy || deadEnd} onClick={() => void act.run("confirm", () => api.scanConfirm(taskId))}>
          {authLink ? "✅ 已完成授权" : "✅ 我已扫码"}
        </button>
        <button className="btn" disabled={!!act.busy} onClick={() => void (async()=>{if(await dialog.confirm({title:"从头重启这个任务？",body:<p>结束当前运行，保留历史和材料，重新从第一步运行。</p>,okText:"从头重启"}))await act.run("restart-task",()=>api.restartTask(taskId));})()}>从头重启</button>
        <button className="btn icon-button" aria-label="全屏扫码画面" title="全屏扫码画面" onClick={fullscreen}><FullscreenIcon/></button>
      </div>
      {deadEnd && <div className="notice warn">重新登录会返回原服务，再获取新的二维码；不会重建任务。</div>}
      {!canRelogin && <div className="notice err">已多次重新登录仍未成功，请人工检查账号授权，或从头重启。</div>}
      {authLink ? <div className="small muted" data-testid="auth-link-tip">申请人用手机相机扫上面的码，在手机上做完认证和授权。做完后这个页面一般会跳转或显示完成，系统自动检测到就继续；检测不到就点「已完成授权」。</div>
      : <div className="small muted">扫码成功后弹框会自动关闭并继续运行；如果没反应，再点「我已扫码」。二维码过期时，开着这个窗口会自动刷新 3 次，之后点「刷新二维码」就有新码。</div>}
    </div>
    <div className="actions"><button className="btn" onClick={() => setOpen(false)}>稍后扫码</button></div>
    </div></div>, document.body)}
    </>
  );
}
